import { useLazyQuery, useQuery } from "@apollo/client";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import CloseIcon from "@mui/icons-material/Close";
import { Chip, Dialog, DialogContent, DialogTitle, Grid } from "@mui/material";
import { Button, InputItem, Pagination, ToastContainer } from "pepsico-ds";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";

import {
  getCodeGroupByIds,
  getCodeGroups,
} from "../../../graphql/queries/codeGroupQueries";
import { getCodeGroupCreationTypeFilter } from "../../../utils/commons";
import {
  categorizeCodeGroups,
  getAllCodeGroupIds,
  isLegacyFormat,
  transformToObjectFormat,
} from "../shared/codeGroupUtils";
import "./uniqueCodeGroupsSelector.scss";
import UniqueCodeGroupsTable from "./UniqueCodeGroupsTable";

const isValidDateValue = (value) => {
  return value && !Number.isNaN(new Date(value).getTime());
};

const isCodeGroupPeriodNotFullyMatchingCampaign = (
  codeActiveStartDate,
  codeActiveEndDate,
  campaignActiveStartDate,
  campaignActiveEndDate
) => {
  if (
    isValidDateValue(codeActiveStartDate) &&
    isValidDateValue(campaignActiveStartDate) &&
    new Date(codeActiveStartDate) > new Date(campaignActiveStartDate)
  ) {
    return true;
  }

  if (
    isValidDateValue(codeActiveEndDate) &&
    isValidDateValue(campaignActiveEndDate) &&
    new Date(codeActiveEndDate) < new Date(campaignActiveEndDate)
  ) {
    return true;
  }

  return false;
};

const UniqueCodeGroupSelectorComponent = ({
  data,
  path,
  handleChange,
  uischema,
}) => {
  const options = uischema.options || {};
  const initialData = useRef(data);
  const { programTimezone } = useContext(GlobalConfigContext);
  const { campaignState } = useContext(CampaignSetupContext);
  const params = useParams();
  const activityDefinition =
    campaignState?.activities?.[params?.activityIndex]?.activityDefinition;
  const activitySlug = activityDefinition?.slug;
  const activityName = activityDefinition?.name;
  const [filter, setFilter] = useState({ searchTerm: "" });
  const [selectedCodeGroupList, setSelectedCodeGroupList] = useState([]);
  const [codeGroups, setCodeGroups] = useState([]);
  const [toastData, setToastData] = useState([]);
  const [showPeriodWarningModal, setShowPeriodWarningModal] = useState(false);
  const [pendingCodeGroup, setPendingCodeGroup] = useState(null);
  let initialFilterData = {
    lifecycleStatus: "ACTIVE",
    limit: 1000,
    ...getCodeGroupCreationTypeFilter(activitySlug, activityName),
    ...options.initialFilter,
  };
  const { data: apiList, loading } = useQuery(getCodeGroups, {
    variables: {
      filter: initialFilterData,
    },
    fetchPolicy: "cache-and-network",
  });
  const [getCodeGroupBasedOnIds] = useLazyQuery(getCodeGroupByIds);

  const [pagination, setPagination] = useState({
    recordsPerPage: 5,
    currentPage: 1,
  });

  useEffect(() => {
    const mapRuleDataToState = async () => {
      const formattedSelectedGroups = [];
      const allIds = getAllCodeGroupIds(initialData?.current);

      if (isLegacyFormat(initialData?.current) && initialData?.current?.length) {
        const newFormatData = transformToObjectFormat(initialData?.current);
        handleChange(path, newFormatData);
      }

      if (allIds?.length && apiList?.listCodeGroups?.length) {
        const response = await getCodeGroupBasedOnIds({
          variables: {
            ids: allIds,
          },
        });
        const codeGroupsWithPartialPeriod = [];
        if (response?.data?.loadCodeGroupsByIds?.length) {
          response?.data?.loadCodeGroupsByIds.forEach((codeGroup) => {
            if (codeGroup) {
              if (
                isCodeGroupPeriodNotFullyMatchingCampaign(
                  codeGroup?.basicSetup?.codeActiveStartDate,
                  codeGroup?.basicSetup?.codeActiveEndDate,
                  campaignState.activeStartDate,
                  campaignState.activeEndDate
                )
              ) {
                codeGroupsWithPartialPeriod.push(codeGroup.basicSetup.codeGroupName);
              }
              formattedSelectedGroups.push(codeGroup);
            }
          });
          if (codeGroupsWithPartialPeriod.length) {
            setToastData([
              {
                type: "warning",
                id: "Code Groups Period Mismatch",
                text: `Validity of one or more Code Groups does not fully match the campaign period: ${codeGroupsWithPartialPeriod.join(", ")}`,
              },
            ]);
          }
        }
        setSelectedCodeGroupList(formattedSelectedGroups);
      }
    };

    mapRuleDataToState();

    if (apiList?.listCodeGroups?.length) {
      setCodeGroups(apiList?.listCodeGroups);
    }
  }, [
    apiList?.listCodeGroups,
    getCodeGroupBasedOnIds,
    campaignState.activeStartDate,
    campaignState.activeEndDate,
    handleChange,
    path,
  ]);

  const ctx = useJsonForms();
  const jsonformFields = ctx.core.schema?.properties;

  useEffect(() => {
    let categorizedData = { default: [], support: [] };
    if (selectedCodeGroupList.length) {
      categorizedData = categorizeCodeGroups(selectedCodeGroupList);
    }
    handleChange(path, categorizedData);

    if (jsonformFields?.campaign_code_group_id) {
      handleChange(
        "campaign_code_group_id",
        selectedCodeGroupList?.[0]?.codeGroupId || null
      );
    }
  }, [
    handleChange,
    path,
    selectedCodeGroupList,
    jsonformFields?.campaign_code_group_id,
  ]);

  const onFilterChange = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    const allCodeGroups = JSON.parse(JSON.stringify(apiList?.listCodeGroups));
    if (value === "") {
      setCodeGroups(allCodeGroups);
    } else {
      setCodeGroups(
        allCodeGroups.filter((codeGroup) =>
          codeGroup?.basicSetup?.codeGroupName
            ?.toLowerCase()
            .includes(value.toLowerCase())
        )
      );
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const shouldShowPeriodWarning = (codeGroup) =>
    isCodeGroupPeriodNotFullyMatchingCampaign(
      codeGroup?.basicSetup?.codeActiveStartDate,
      codeGroup?.basicSetup?.codeActiveEndDate,
      campaignState.activeStartDate,
      campaignState.activeEndDate
    );

  const closePeriodWarningModal = () => {
    setShowPeriodWarningModal(false);
    setPendingCodeGroup(null);
  };

  const confirmPeriodMismatchSelection = () => {
    if (pendingCodeGroup) {
      setSelectedCodeGroupList([pendingCodeGroup]);
    }
    closePeriodWarningModal();
  };

  const onSelectCodeGroup = (codeGroup) => {
    let selectedRows = selectedCodeGroupList
      ? JSON.parse(JSON.stringify(selectedCodeGroupList))
      : [];
    const index = selectedRows?.findIndex(
      (item) => item.codeGroupId === codeGroup.codeGroupId
    );
    if (index > -1) {
      selectedRows = selectedRows?.filter(
        (item) => item.codeGroupId !== codeGroup.codeGroupId
      );
    } else {
      if (shouldShowPeriodWarning(codeGroup)) {
        setPendingCodeGroup(codeGroup);
        setShowPeriodWarningModal(true);
        return;
      }
      selectedRows = [codeGroup];
    }
    setSelectedCodeGroupList(selectedRows);
  };

  const getPaginatedCodeGroups = () => {
    const start = (pagination.currentPage - 1) * pagination.recordsPerPage;
    const end = start + pagination.recordsPerPage;
    return codeGroups.slice(start, end);
  };

  return (
    <div>
      <ToastContainer
        data={toastData}
        position="top"
        removeToast={() => setToastData([])}
        showActionIcon
      />
      <Dialog
        className="create-winning-schedules-modal"
        open={showPeriodWarningModal}
        scroll="paper"
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" direction="row">
            <Grid item xs={10}>
              <h2>Confirm to continue</h2>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end">
              <CloseIcon
                onClick={closePeriodWarningModal}
                className="cursor-pointer"
              />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <div className="my-5">
            <h5>
              Validity of one or more Code Groups does not fully match the campaign
              period.
            </h5>
            <p className="font-xs my-2">
              Promo codes will only work during their own activation window.
            </p>
            <p className="font-xs my-2">Do you want to add it anyway?</p>
          </div>
          <div className="mt-5 display-flex justify-content-end align-items-center">
            <Button
              onClick={closePeriodWarningModal}
              variant="tertiary"
              className="mr-3"
              size="medium"
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={confirmPeriodMismatchSelection}
              size="medium"
            >
              Add anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="display-flex">
        <div style={{ width: "75%" }}>
          <div
            style={{
              padding: "0px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "16px",
              marginBottom: "0.85rem",
              flex: 0.2,
            }}
          >
            <InputItem
              placeholder="Type a search keyword..."
              trailingIcon="search"
              size="medium"
              value={filter.searchTerm}
              style={{ width: "50%" }}
              className="input-search"
              onChange={(e) => onFilterChange("searchTerm", e.target.value)}
            />
          </div>
          {!loading ? (
            <>
              <UniqueCodeGroupsTable
                codeGroupsData={getPaginatedCodeGroups()}
                selectedCodeGroupsList={selectedCodeGroupList}
                loading={loading}
                programTimezone={programTimezone}
                onSelectCodeGroup={(newCodeGroup) => onSelectCodeGroup(newCodeGroup)}
              />
              {codeGroups.length > 0 && (
                <Pagination
                  currentPage={pagination.currentPage}
                  onUpdate={(page) => {
                    setPagination((prev) => ({ ...prev, currentPage: page }));
                  }}
                  pageCount={Math.ceil(
                    codeGroups.length / pagination.recordsPerPage
                  )}
                  size="medium"
                  variant="number"
                />
              )}
            </>
          ) : (
            <div>Loading...</div>
          )}
        </div>
        <div
          style={{
            width: "25%",
            padding: "0.5rem",
            backgroundColor: "#F3F7FD",
            marginLeft: "1%",
          }}
        >
          <div className="display-flex gap-2 mt-16" style={{ flexWrap: "wrap" }}>
            {selectedCodeGroupList?.map((codeGroup, index) => (
              <Chip
                key={index}
                id={`codeGroup-${codeGroup?.codeGroupId}`}
                className={
                  shouldShowPeriodWarning(codeGroup)
                    ? "code-group-period-mismatch"
                    : ""
                }
                label={codeGroup?.basicSetup?.codeGroupName}
                sx={{
                  borderRadius: "8px",
                  fontSize: "12px",
                  backgroundColor: "#B3DACB",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const UniqueCodeGroupSelectorControl = withJsonFormsControlProps(
  UniqueCodeGroupSelectorComponent
);
